import styles from './external-access-dialog.module.scss';
import { useContext, useState } from 'react';
import { ContactListContext } from '../../customer-context/customer-context';
import { useImmer } from 'use-immer';
import { ValueFormatterParams } from 'ag-grid-community';
import { Checkbox, CheckboxLabel, CompanySearch, CreateHeaders, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, PrimaryButton, SnackBarContext, textHeader } from '@frontend-workspace/react-shared-lib';
import { IPersonsOnLicense } from '../../_models/persons-on-license';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { use } from 'i18next';
import { useMakeRequestHook } from '../../requestUtils';

/* eslint-disable-next-line */
export interface ExternalAccessDialogProps {
  open: boolean,
  onClose: () => void,
  initialPersonsOnLicense: IPersonsOnLicense[],
  initialSelectedCompanyNumber: string,
  licenseNumber: number,
  onSelectedCompanyChange: (c: string) => void
}

export function ExternalAccessDialog(props: ExternalAccessDialogProps) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [dateValue, setDateValue] = useState<Date>(new Date());
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [selectedCompanyNumber, setSelectedCompanyNumber] = useState<string>(props.initialSelectedCompanyNumber);
  const contactList = useContext(ContactListContext);
  const [initialPersons, updateInitialPersons] = useImmer<IPersonsOnLicense[]>(props.initialPersonsOnLicense);
  const [personsOnLicense, updatePersonsOnLicense] = useImmer<IPersonsOnLicense[]>(props.initialPersonsOnLicense);

  const handleSendEmail = (event: React.ChangeEvent<Element>, checked: boolean) => setSendEmail(checked);

  const handleCompanyChange = (companyNumber: string) => {
    setSelectedCompanyNumber(companyNumber);
    const contact = contactList.find(c => c.number === companyNumber);
    if (contact) {
      makeRequest(`companies/${contact.number}/licenses/${props.licenseNumber}/action/getLicenseAccesses`).then(result => {
        updateInitialPersons(draft => result);
        updatePersonsOnLicense(draft => result);
      }).catch(() => setSnackbarMessage({ content: t('externalAccess.couldNotSelectCompanyToCustomer'), type: "error" }));
      props.onSelectedCompanyChange(contact.number);
    }
  };

  const onAccessChange = (userName: string, isCoordinatorChange: boolean, newValue: boolean) => {
    updatePersonsOnLicense(draft => {
      const changedPerson = draft.find(person => person.email === userName);
      if (changedPerson) {
        if (isCoordinatorChange) {
          changedPerson.isCoordinator = newValue;
        }
        if (!isCoordinatorChange) {
          changedPerson.hasAccess = newValue;
        }
      }
    });
  };

  const changeAccessRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      onAccessChange(params.data.email, false, checked);
    }
    return <Checkbox
      checked={params.value}
      onChange={handleOnClick}
    />
  };

  const changeCoordinatorRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      onAccessChange(params.data.email, true, checked);
    }
    return <Checkbox
      checked={params.value}
      onChange={handleOnClick}
    />
  };

  const handleAccessChange = () => {
    const changedPersons = personsOnLicense.filter(p => {
      const initial = initialPersons.find(ip => ip.email === p.email);
      return initial && initial.hasAccess !== p.hasAccess;
    }).map(p => ({
      licenseNumber: props.licenseNumber,
      giveAccess: p.hasAccess,
      email: p.email,
      isCoordinator: p.isCoordinator,
    }));

    makeRequest(`v2/licenses/action/changeLicenseAccess`, 'PUT', {
      accessChanges: changedPersons,
      activationDate: dateValue,
      sendEmail: sendEmail,
    }).then(result => {
      setSnackbarMessage({ content: t('standard.changesLicenseAccesses'), type: "info", });
      props.onClose();
    });
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        Eksterne lisensrettigheter
      </DialogTitle>
      <DialogContent>
        <div className="d-flex flex-row align-items-end flex-wrap mb-0 mb-md-4">
          <div className='mb-3 mb-md-0 me-4 flex-fill'>
            <label>{t('externalAccess.selectCompanyToCustomer')}</label>
            <CompanySearch
              selectedCompanyNumber={selectedCompanyNumber}
              companyList={contactList}
              onChange={handleCompanyChange}
            />
          </div>
          <div className='mb-3 mb-md-0 me-4'>
            <DatePicker
              inputFormat="dd.MM.yyyy"
              label={t('standard.executionDate')}
              value={dateValue}
              onChange={(newDateValue: any) => {
                setDateValue(newDateValue);
              }}
            />
          </div>
          <div className='mb-3 mb-md-0'>
            <CheckboxLabel
              label={t('standard.sendEmail')}
              checked={sendEmail}
              onChange={handleSendEmail}
            />
          </div>
        </div>
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("firstName", t('standard.firstName'), HeaderSizeEnum.LARGE),
            textHeader("lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
            textHeader("userName", t('standard.username'), HeaderSizeEnum.EXTRA_LARGE),
            textHeader("hasAccess", t('standard.license'), HeaderSizeEnum.MEDIUM, {
              renderer: changeAccessRenderer
            }),
            textHeader("isCoordinator", t('standard.licensingCoordinator'), HeaderSizeEnum.SMALL, {
              renderer: changeCoordinatorRenderer
            })
          ])}
          data={personsOnLicense}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleAccessChange}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default ExternalAccessDialog;